/**
 * Lightweight, privacy-respecting browser fingerprint.
 *
 * Combines stable browser/device characteristics into a single string,
 * SHA-256 hashes it client-side, and returns the first 32 hex chars.
 * The full payload never leaves the device — only the hash does.
 *
 * Inputs (all otherwise harmless):
 *   - userAgent
 *   - screen resolution + colorDepth + pixelRatio
 *   - timezone (Intl.DateTimeFormat resolved options)
 *   - hardwareConcurrency + deviceMemory (where available)
 *   - canvas-rendered text (browsers + GPU produce subtly different
 *     anti-aliasing / kerning, which gives ~85% uniqueness in practice
 *     without needing audio/webgl probes that some privacy tools
 *     actively block)
 *
 * Deliberate non-inputs (would tip into "fingerprinting" territory
 * privacy tools would flag/block):
 *   - WebGL renderer string
 *   - Audio context fingerprint
 *   - Battery / connection APIs
 *   - Installed fonts
 *
 * Result: stable for ~30 days for the same device/browser, changes
 * with major browser version updates or canvas-anti-spoofing extensions
 * (which is a feature, not a bug — those users were trying not to be
 * tracked, and our rate-limit just won't apply).
 */
async function sha256Hex(s) {
  const buf = new TextEncoder().encode(s);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function safeCanvasFingerprint() {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 240;
    canvas.height = 60;
    const ctx = canvas.getContext("2d");
    if (!ctx) return "no-ctx";
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("CoParent\u2693console", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("CoParent\u2693console", 4, 17);
    return canvas.toDataURL();
  } catch {
    return "canvas-blocked";
  }
}

function collectSignals() {
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const scr = typeof screen !== "undefined" ? screen : {};
  const tz = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    } catch {
      return "";
    }
  })();
  return [
    nav.userAgent || "",
    nav.language || "",
    `${scr.width || 0}x${scr.height || 0}@${scr.colorDepth || 0}`,
    typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
    tz,
    nav.hardwareConcurrency || 0,
    nav.deviceMemory || 0,
    safeCanvasFingerprint(),
  ].join("|");
}

let _cached = null;

/**
 * Compute (or return the cached) fingerprint hash. Safe to call
 * repeatedly — only does the canvas + hash work once per page load.
 */
export async function getClientFingerprint() {
  if (_cached) return _cached;
  try {
    const raw = collectSignals();
    const hash = await sha256Hex(raw);
    _cached = hash.slice(0, 32);
    return _cached;
  } catch {
    return null;
  }
}
